import React from "react"
import trans, { isTrans } from "trans"
import { Badge, Grid, Icon, Theme } from "@bdfx/mergado-ui-kit"
import { AITemplatType, RENDERED_TYPE_ANIM_GIF, RENDERED_TYPE_AUTOWMREMOVE, RENDERED_TYPE_BGREMOVE, RENDERED_TYPE_INPAINTING, RENDERED_TYPE_MANUAL_BGREMOVE_EDITED, RENDERED_TYPE_MANUAL_WMREMOVE, RENDERED_TYPE_OUTPAINTING, RENDERED_TYPE_UPSCALE, RENDERED_TYPES_COLORS } from "products/types"
import { getLocale } from "app/helpers/session"
import { IconAIParams, IconInpainting, IconOutpainting, IconRemoveBackground, IconRemoveWatermark, IconSimilarity, IconUpscale } from "app/icons"
import { getAddOutpaintedSuffix } from "shape-edit/forms/helpers"

const locale = getLocale().replace('_', '-')

export const TemplateBadges = ({ template, showIcons = false, showText = true, showInline = true, showRegular = false }) => {
    const icon = getTemplateTypeIcon(template.type)
    const text = getTemplateTypeTranslated(template.type)
    const color = getTemplateTypeColor(template.type)

    const sensitivity = template.type === RENDERED_TYPE_AUTOWMREMOVE ? template.effect_sensitivity || 'HIGH' : template.type === RENDERED_TYPE_BGREMOVE ? template.effect_sensitivity || 'MEDIUM' : null
    const addSuffix = template.type === RENDERED_TYPE_OUTPAINTING ? getAddOutpaintedSuffix(template.outpaint_over, template.overscan, template.translate_x, template.translate_y) : null

    const iconSize = 16
    const iconColor = '#ffffff'

    return <Grid style={{ display: showInline ? 'inline-grid' : 'block' }} autoFlow={showInline ? "column" : undefined} inline={showInline} gap="0 5px">
        {!template.active &&
            <Badge small={true} color="warning" icon="pause">{trans("not_active")}</Badge>
        }

        {!!(template.active_from || template.active_to) &&
            <Badge small={true} color="warning" icon="lap-timer-radix">
                {trans("active")}{" "}
                {template.active_from && <React.Fragment> {trans('from_date')} {new Date(template.active_from).toLocaleDateString(locale)}</React.Fragment>}
                {template.active_to && <React.Fragment> {trans('to_date')} {new Date(template.active_to).toLocaleDateString(locale)}</React.Fragment>}
            </Badge>
        }

        {!!template.type &&
            <Badge small={true} color={color}>
                {showIcons && icon} {showText && text} {sensitivity && <span>({trans('sensitivity')}: {trans(sensitivity)})</span>}
                {addSuffix && addSuffix.length > 0 && <span title={trans('outpaint_over') + ' ' + addSuffix.replace('_', ' ')}><Icon type={'expand'} color={iconColor} /></span>}
            </Badge>
        }

        {(showRegular && !template.type) &&
            <Badge small={true} color={color}>
                <Icon type={'rectangle-light'} color={iconColor} /> <span>{trans('steps_create_blank')}</span>
            </Badge>
        }

        {!!template.is_anim_gif &&
            <Badge small={true} color="info">
                {showIcons && <Icon type={'film'} color={iconColor} size={iconSize} />} {trans("is_anim_gif")}
            </Badge>
        }

        {!!template.is_collage &&
            <Badge small={true} color="info">
                {showIcons && <Icon type={'grid-light'} color={iconColor} size={iconSize} />} {trans("is_collage")}
            </Badge>
        }

        {!!template.is_autocrop &&
            <Badge small={true} color="info">
                {showIcons && <Icon type={'crop-light'} color={iconColor} size={iconSize} />} {trans("is_autocrop")}
            </Badge>
        }

        {!!template.visual_similarity_enabled &&
            <Badge small={true} color="yellow" title={`n=${template.visual_similarity_n}, t=${template.visual_similarity_threshold}, l=${template.visual_similarity_templateids}`}>
                {showIcons && <IconSimilarity color={'#3a3a3a'} size={iconSize} />} {trans("visual_similarity_enabled")}
            </Badge>
        }

        {!!template.ai_params_enabled &&
            <Badge small={true} color="info">
                {showIcons && <IconAIParams color={iconColor} size={iconSize} />} {trans("ai_params")}
            </Badge>
        }
    </Grid>
}

export function getTemplateTypeTranslated(type: AITemplatType): string
{
    const text = type ? `template_type_${type}` : 'graphical_template'

    return isTrans(text) ? trans(text) : ''

}

export function getTemplateTypeColor(type: AITemplatType): string
{
    return RENDERED_TYPES_COLORS[type] ?? Theme.blue
}

export function getTemplateTypeIcon(type: AITemplatType, size: number = 16, color: string = '#ffffff') : JSX.Element
{
    switch (type) {
        case RENDERED_TYPE_UPSCALE:
            return <IconUpscale color={color} size={size} />
        case RENDERED_TYPE_MANUAL_BGREMOVE_EDITED:
        case RENDERED_TYPE_BGREMOVE:
            return <IconRemoveBackground color={color} size={size}  />
        case RENDERED_TYPE_MANUAL_WMREMOVE:
        case RENDERED_TYPE_AUTOWMREMOVE:
            return <IconRemoveWatermark color={color} size={size} />
        case RENDERED_TYPE_OUTPAINTING:
            return <IconOutpainting color={color} size={size} />
        case RENDERED_TYPE_INPAINTING:
            return <IconInpainting color={color} size={size} />
        case RENDERED_TYPE_ANIM_GIF:
            return <Icon type={'film'} color={color} size={size} />
    }

    return null
}
